/**
 * Utilitaires pour le ticket de refoulement
 */

import { RefoulementTicketData } from '@/types/refoulementTicket';

/**
 * Génère un QR code en base64 pour le ticket
 */
const generateQRCode = (data: string): string => {
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(data)}&format=png&ecc=M`;
  return qrUrl;
};

/**
 * Génère un QR code de fallback en cas de problème de connexion
 */
const generateFallbackQRCode = (data: string): string => {
  return `data:image/svg+xml;base64,${btoa(`
    <svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">
      <rect width="100" height="100" fill="white" stroke="black" stroke-width="1"/>
      <text x="50" y="50" text-anchor="middle" font-family="monospace" font-size="8" fill="black">QR</text>
      <text x="50" y="60" text-anchor="middle" font-family="monospace" font-size="6" fill="black">${data.substring(0, 10)}...</text>
    </svg>
  `)}`;
};

/**
 * Génère le HTML d'impression du ticket de refoulement
 */
export const generateRefoulementPrintHTML = (ticketData: RefoulementTicketData): string => {
  const formatWeight = (weight: number) => `${weight.toLocaleString()} kg`;
  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('fr-FR');
  const formatDateTime = (dateString: string) => new Date(dateString).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const isValidValue = (value: any): boolean => {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    if (typeof value === 'number') {
      return !isNaN(value);
    }
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    return true;
  };

  // Génération du QR code
  const qrData = `Refoulement: ${ticketData.numero_refoulement}|Pesee: ${ticketData.pesee.reference || ticketData.pesee.code}|Date: ${formatDate(ticketData.date_refoulement)}`;
  const qrCodeUrl = generateQRCode(qrData);
  const fallbackQRCode = generateFallbackQRCode(qrData);

  const getCriticiteClass = (criticite: string) => {
    switch (criticite?.toLowerCase()) {
      case 'critique': return 'critique';
      case 'haute': return 'haute';
      case 'moyenne': return 'moyenne';
      case 'faible': return 'faible';
      default: return 'default';
    }
  };

  return `
    <!DOCTYPE html>
    <html lang="fr">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Ticket de Refoulement - ${ticketData.numero_refoulement}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Arial', sans-serif;
          font-size: 12px;
          line-height: 1.4;
          color: #000;
          background: white;
          padding: 8px 40px;
          margin: 0;
        }
        
        .ticket-container {
          max-width: 100%;
          margin: 0 auto;
          border: 2px solid #dc2626;
          padding: 16px 40px;
          background: white;
        }
        
        .header {
          text-align: center;
          border-bottom: 2px solid #dc2626;
          padding-bottom: 8px;
          margin-bottom: 10px;
        }
        
        .logo-section {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;
          gap: 15px;
        }
        
        .logo {
          display: flex;
          align-items: center;
        }
        
        .logo-circle {
          width: 20px;
          height: 20px;
          background: #16a34a;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 8px;
          color: white;
          font-size: 10px;
          font-weight: bold;
        }
        
        .logo-text {
          font-size: 11px;
          font-weight: bold;
        }
        
        .logo-subtext {
          font-size: 9px;
          color: #666;
        }
        
        .qr-code {
          text-align: center;
        }
        
        .qr-code img {
          width: 80px;
          height: 80px;
          border: 1px solid #ddd;
          border-radius: 4px;
        }
        
        .main-title {
          font-size: 24px;
          font-weight: bold;
          color: #dc2626;
          margin: 10px 0 5px 0;
        }
        
        .subtitle {
          font-size: 16px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 5px;
        }
        
        .ticket-number {
          font-size: 20px;
          font-family: monospace;
          font-weight: bold;
          color: #dc2626;
          margin-top: 5px;
        }
        
        .info-section {
          margin: 15px 0;
        }
        
        .section-title {
          font-size: 14px;
          font-weight: bold;
          color: #111827;
          border-bottom: 1px solid #d1d5db;
          padding-bottom: 4px;
          margin-bottom: 8px;
        }
        
        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }
        
        .info-item {
          display: flex;
          justify-content: space-between;
          padding: 4px 0;
        }
        
        .info-label {
          color: #6b7280;
          font-weight: 500;
        }
        
        .info-value {
          color: #111827;
          font-weight: 600;
        }
        
        .info-value-red {
          color: #dc2626;
          font-weight: 600;
        }
        
        .motif-card {
          border: 2px solid;
          border-radius: 6px;
          padding: 10px;
          margin-bottom: 10px;
        }
        
        .motif-card.critique {
          border-color: #dc2626;
          background-color: #fef2f2;
        }
        
        .motif-card.haute {
          border-color: #ea580c;
          background-color: #fff7ed;
        }
        
        .motif-card.moyenne {
          border-color: #eab308;
          background-color: #fefce8;
        }
        
        .motif-card.faible {
          border-color: #16a34a;
          background-color: #f0fdf4;
        }
        
        .motif-card.default {
          border-color: #6b7280;
          background-color: #f9fafb;
        }
        
        .motif-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
        }
        
        .motif-title {
          font-weight: 600;
          color: #111827;
        }
        
        .motif-badge {
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
        }
        
        .motif-badge.critique {
          background-color: #fee2e2;
          color: #dc2626;
        }
        
        .motif-badge.haute {
          background-color: #fed7aa;
          color: #ea580c;
        }
        
        .motif-badge.moyenne {
          background-color: #fef08a;
          color: #eab308;
        }
        
        .motif-badge.faible {
          background-color: #dcfce7;
          color: #16a34a;
        }
        
        .motif-badge.default {
          background-color: #f3f4f6;
          color: #6b7280;
        }
        
        .motif-description {
          font-size: 11px;
          color: #374151;
          margin-bottom: 6px;
        }
        
        .motif-details {
          font-size: 10px;
          color: #6b7280;
        }
        
        .observations {
          background-color: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 10px;
          margin-top: 10px;
        }
        
        .observations-text {
          font-size: 11px;
          color: #374151;
          white-space: pre-wrap;
        }
        
        .footer {
          margin-top: 20px;
          padding-top: 10px;
          border-top: 1px solid #e5e7eb;
          text-align: center;
          font-size: 10px;
          color: #6b7280;
        }
        
        @media print {
          @page {
            size: A4 portrait;
            margin: 10mm 25mm;
          }
          body {
            margin: 0;
            padding: 0 40px;
          }
          .ticket-container {
            border: none;
            padding: 0 40px;
          }
        }
      </style>
    </head>
    <body>
      <div class="ticket-container">
        <div class="header">
          <div class="logo-section">
            <div class="logo">
              <img src="/images/cca-logo.svg" alt="Logo CCA" style="height: 40px; width: auto; margin-right: 8px; object-fit: contain;" onerror="this.style.display='none';" />
            </div>
            
            <div class="qr-code">
              <img src="${qrCodeUrl}" alt="QR Code" onerror="this.src='${fallbackQRCode}'; this.onerror=null;" />
              <div style="font-size: 9px; color: #666; margin-top: 4px;">QR Code</div>
            </div>
            
            <div style="text-align: right;">
              <img src="/images/logo-pontcita.png" alt="Logo Pont CITA" style="height: 35px; width: auto; object-fit: contain;" onerror="this.style.display='none';" />
            </div>
          </div>
          
          <div style="text-align: center; font-size: 11px; margin: 8px 0;">
            <div><span style="color: #dc2626; font-weight: 600;">Date refoulement:</span> ${formatDate(ticketData.date_refoulement)}</div>
            <div><span style="color: #dc2626; font-weight: 600;">Pesée:</span> ${ticketData.pesee.reference || ticketData.pesee.code}</div>
            <div><span style="color: #dc2626; font-weight: 600;">Campagne:</span> ${ticketData.campaignYear || new Date().getFullYear()}</div>
            <div><span style="color: #dc2626; font-weight: 600;">Agent:</span> ${ticketData.agent_refoulement_nom || ticketData.agent_refoulement_username || ticketData.agent_refoulement}</div>
          </div>
          
          <div class="main-title">TICKET DE REFOULEMENT</div>
          <div class="subtitle">
            <span style="margin-right: 20px;">PESEE</span>
            <span>REFOULEE</span>
          </div>
          <div class="ticket-number">${ticketData.numero_refoulement}</div>
        </div>

        <!-- Informations de la pesée -->
        <div class="info-section">
          <div class="section-title">Informations de la pesée</div>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Référence:</span>
              <span class="info-value-red">${ticketData.pesee.reference || ticketData.pesee.code}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Poids brut:</span>
              <span class="info-value-red">${formatWeight(ticketData.pesee.grossWeight)}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Véhicule:</span>
              <span class="info-value">${ticketData.pesee.vehicle || 'Non spécifié'}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Poids tare:</span>
              <span class="info-value">${formatWeight(ticketData.pesee.tareWeight)}</span>
            </div>
            ${ticketData.pesee.trailerRegistration ? `
            <div class="info-item">
              <span class="info-label">Remorque:</span>
              <span class="info-value">${ticketData.pesee.trailerRegistration}</span>
            </div>
            ` : ''}
            <div class="info-item">
              <span class="info-label">Poids net:</span>
              <span class="info-value-red" style="font-size: 14px; font-weight: bold;">${formatWeight(ticketData.pesee.netWeight)}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Chauffeur:</span>
              <span class="info-value">${ticketData.pesee.driver || 'Non spécifié'}</span>
            </div>
            ${ticketData.pesee.bagWeight ? `
            <div class="info-item">
              <span class="info-label">Poids sacs:</span>
              <span class="info-value">${formatWeight(ticketData.pesee.bagWeight)}</span>
            </div>
            ` : ''}
            ${ticketData.pesee.transporterName ? `
            <div class="info-item">
              <span class="info-label">Transporteur:</span>
              <span class="info-value">${ticketData.pesee.transporterName}</span>
            </div>
            ` : ''}
            <div class="info-item">
              <span class="info-label">Produit:</span>
              <span class="info-value">${ticketData.pesee.product || 'Non spécifié'}</span>
            </div>
            ${ticketData.pesee.provenance ? `
            <div class="info-item">
              <span class="info-label">Provenance:</span>
              <span class="info-value">${ticketData.pesee.provenance}</span>
            </div>
            ` : ''}
            <div class="info-item">
              <span class="info-label">Site:</span>
              <span class="info-value">${ticketData.pesee.site || 'Non spécifié'}</span>
            </div>
            ${ticketData.pesee.destination ? `
            <div class="info-item">
              <span class="info-label">Destination:</span>
              <span class="info-value">${ticketData.pesee.destination}</span>
            </div>
            ` : ''}
            ${ticketData.pesee.tenant ? `
            <div class="info-item">
              <span class="info-label">Tenant:</span>
              <span class="info-value">${ticketData.pesee.tenant}</span>
            </div>
            ` : ''}
            <div class="info-item">
              <span class="info-label">Date pesée brut:</span>
              <span class="info-value">${formatDateTime(ticketData.pesee.grossAt)}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Date pesée tare:</span>
              <span class="info-value">${formatDateTime(ticketData.pesee.tareAt)}</span>
            </div>
          </div>
        </div>

        ${ticketData.pesee.analyse_qualite ? `
        <!-- Analyse qualité -->
        <div class="info-section">
          <div class="section-title">Analyse qualité</div>
          <div class="info-grid">
            ${ticketData.pesee.analyse_qualite.grainnage ? `
            <div class="info-item">
              <span class="info-label">Grainnage:</span>
              <span class="info-value">${ticketData.pesee.analyse_qualite.grainnage}</span>
            </div>
            ` : ''}
            ${ticketData.pesee.analyse_qualite.humidite !== undefined ? `
            <div class="info-item">
              <span class="info-label">Taux d'humidité:</span>
              <span class="info-value">${ticketData.pesee.analyse_qualite.humidite}%</span>
            </div>
            ` : ''}
            ${ticketData.pesee.analyse_qualite.quality !== undefined ? `
            <div class="info-item">
              <span class="info-label">Qualité:</span>
              <span class="info-value">${ticketData.pesee.analyse_qualite.quality}</span>
            </div>
            ` : ''}
            ${ticketData.pesee.analyse_qualite.quality_level ? `
            <div class="info-item">
              <span class="info-label">Niveau de qualité:</span>
              <span class="info-value">${ticketData.pesee.analyse_qualite.quality_level}</span>
            </div>
            ` : ''}
            ${ticketData.pesee.analyse_qualite.quality_decision ? `
            <div class="info-item" style="grid-column: 1 / -1;">
              <span class="info-label">Décision qualité:</span>
              <span class="info-value">${ticketData.pesee.analyse_qualite.quality_decision}</span>
            </div>
            ` : ''}
          </div>
        </div>
        ` : ''}

        <!-- Motifs de refoulement -->
        <div class="info-section">
          <div class="section-title">Motifs de refoulement</div>
          ${ticketData.motifs && ticketData.motifs.length > 0 ? ticketData.motifs.map((motif: any, index: number) => {
            // Normaliser les données du motif pour l'affichage
            // Prioriser libelle, puis nom, puis name, puis id
            // Remplacer les underscores par des espaces
            const cleanLibelle = (str: string) => str ? str.replace(/_/g, ' ') : '';
            const motifLibelleRaw = motif?.libelle || motif?.nom || motif?.name || (typeof motif === 'string' ? motif : String(motif?.id || ''));
            const motifLibelle = cleanLibelle(motifLibelleRaw);
            
            return `
            <div style="display: flex; align-items: start; margin-bottom: 8px;">
              <span style="color: #dc2626; font-weight: bold; margin-right: 8px;">*</span>
              <span style="color: #111827;">${motifLibelle}</span>
            </div>
          `;
          }).join('') : '<p class="text-sm text-gray-600">Aucun motif spécifié</p>'}
        </div>

        ${ticketData.observations ? `
        <!-- Observations -->
        <div class="info-section">
          <div class="section-title">Observations</div>
          <div class="observations">
            <div class="observations-text">${ticketData.observations}</div>
          </div>
        </div>
        ` : ''}

        <!-- Émargement -->
        <div style="margin-top: 40px; padding-top: 20px; border-top: 2px solid #d1d5db;">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 40px;">
            <div style="text-align: center;">
              <div style="border-top: 2px solid #9ca3af; padding-top: 8px; width: 128px; margin: 0 auto 8px;"></div>
              <div style="font-weight: bold; color: #111827; font-size: 12px;">AGENT REFOULEMENT</div>
              <div style="font-size: 11px; color: #6b7280; margin-top: 4px;">${ticketData.refoulement?.agent_refoulement_nom || ''}</div>
            </div>
            <div style="text-align: center;">
              <div style="border-top: 2px solid #9ca3af; padding-top: 8px; width: 128px; margin: 0 auto 8px;"></div>
              <div style="font-weight: bold; color: #111827; font-size: 12px;">VISA CLIENT / FOURNISSEUR</div>
              <div style="font-size: 11px; color: #6b7280; margin-top: 4px;"></div>
            </div>
          </div>
        </div>

        <div class="footer">
          Généré le ${ticketData.generated_at ? formatDateTime(ticketData.generated_at) : formatDateTime(new Date().toISOString())}
        </div>
      </div>
    </body>
    </html>
  `;
};
